import type { components } from '@multaqa/api-client'
import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useRef } from 'react'
import { toConditionQuery } from '@/components/condition-filter'
import { api } from '@/lib/api'
import type { JournalEntryBody, JournalEntryFilters } from './accounting-journal-types'

export type {
  JournalEntryBody,
  JournalEntryFilters,
  JournalEntryStatus,
  JournalEntryType,
  JournalLineBody,
  JournalSortColumn,
} from './accounting-journal-types'
export { JOURNAL_ENTRY_STATUSES, JOURNAL_ENTRY_TYPES } from './accounting-journal-types'

/**
 * A mutation whose request body is the current company, read at CALL time
 * rather than at hook-creation time.
 *
 * TanStack Query hooks capture their closure at render; a screen whose `me`
 * query resolves after first render would otherwise send the mutation with an
 * empty X-Company-Id. The ref holds the latest companyId between renders.
 */
function useFreshCompanyId(companyId: string | null): () => string {
  const ref = useRef(companyId)
  ref.current = companyId
  return () => ref.current ?? ''
}

/** Peel the `{ data: … }` envelope a JsonResource adds. */
function unwrap<T>(wrapper: { data?: unknown }, endpoint: string): T {
  if (wrapper.data === undefined || wrapper.data === null) {
    throw new Error(`Malformed ${endpoint} response: missing data envelope.`)
  }
  return wrapper.data as T
}

export type JournalEntry = components['schemas']['JournalEntryResource']
export type FiscalPeriod = components['schemas']['FiscalPeriodResource']

/** Rows per register page. The API caps `per_page` at 200. */
export const JOURNAL_REGISTER_PER_PAGE = 25

/** One page of the register, plus the paginator's own arithmetic. */
export interface JournalEntryPage {
  entries: JournalEntry[]
  /** The filtered total across every page — what "N entries" actually means. */
  total: number
  currentPage: number
  lastPage: number
  perPage: number
}

/**
 * A page of the register, filtered by the SERVER.
 *
 * The endpoint answers a paginator (`data` + `meta`) and accepts every filter
 * the screen offers: `search`, `from`, `to`, `source_type`, `status`,
 * `entry_type`, `account_id`, `cost_center_id`, `is_adjusting_entry`,
 * `min_amount`, `max_amount`, `sort`, `dir`, `page`, `per_page`. Filtering on
 * the client could only ever answer about the page already loaded, so a
 * company past 200 entries silently got the wrong set.
 *
 * `lines.account` is eager-loaded, so each row can show its debit and credit
 * columns.
 *
 * It is NOT how a single entry is found. `useJournalEntry` reads the show
 * endpoint for that, because an entry older than the current page is invisible
 * here and used to render as "not found".
 */
export function useJournalEntries(
  companyId: string | null,
  filters: JournalEntryFilters = {},
  options?: Omit<UseQueryOptions<JournalEntryPage, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<JournalEntryPage, Error>({
    // The filters are part of the key: a new filter is a new question, and the
    // previous answer must not be served as this one's.
    queryKey: ['companies', companyId, 'accounting', 'journal-entries', filters] as const,
    enabled: Boolean(companyId),
    ...options,
    queryFn: async (): Promise<JournalEntryPage> => {
      if (!companyId) {
        return {
          entries: [],
          total: 0,
          currentPage: 1,
          lastPage: 1,
          perPage: JOURNAL_REGISTER_PER_PAGE,
        }
      }

      const response = await api.GET('/accounting/journal-entries', {
        headers: { 'X-Company-Id': companyId },
        params: {
          query: {
            search: filters.q?.trim() || undefined,
            from: filters.from,
            to: filters.to,
            status: filters.status,
            source_type: filters.source,
            'journal_ids[]': filters.journal_ids,
            entry_type: filters.entryType,
            account_id: filters.account,
            cost_center_id: filters.costCenter,
            is_adjusting_entry: filters.adjusting,
            // Schema artifact: Scramble publishes a `numeric` rule as a JSON
            // number, while money crosses the wire as a decimal STRING
            // (ADR-0012). The cast keeps the typed client honest without a
            // `Number()` that would pull a 19-digit bound through a double.
            min_amount: filters.minAmount as unknown as number | undefined,
            max_amount: filters.maxAmount as unknown as number | undefined,
            sort: filters.sort === 'date' ? 'entry_date' : filters.sort,
            dir: filters.dir,
            page: filters.page,
            per_page: filters.perPage ?? JOURNAL_REGISTER_PER_PAGE,
            ...toConditionQuery(filters.conditions ?? [], filters.match ?? 'all'),
          },
        },
      })
      if (response.error) throw response.error

      const page = response.data
      if (!page) throw new Error('Malformed journal-entries response: missing data envelope.')

      return {
        entries: page.data ?? [],
        total: page.meta?.total ?? page.data?.length ?? 0,
        currentPage: page.meta?.current_page ?? 1,
        lastPage: page.meta?.last_page ?? 1,
        perPage: page.meta?.per_page ?? JOURNAL_REGISTER_PER_PAGE,
      }
    },
  })
}

/**
 * One journal entry by id, straight from the server.
 *
 * `GET /accounting/journal-entries/{entry}` (F-027). The viewers used to resolve
 * an id by scanning the index, which answers with one page: an entry older than
 * that page rendered as "not found" even though it exists and is posted. A
 * lookup that can only find recent rows is not a lookup.
 *
 * Kept separate from the index cache on purpose. Sharing a key would let a
 * filtered or paginated index evict a single entry a dialog is still showing.
 */
export function useJournalEntry(
  companyId: string | null,
  entryId: string | null,
  options?: Omit<UseQueryOptions<JournalEntry, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<JournalEntry, Error>({
    queryKey: ['companies', companyId, 'accounting', 'journal-entry', entryId] as const,
    enabled: Boolean(companyId && entryId),
    ...options,
    queryFn: async (): Promise<JournalEntry> => {
      if (!companyId || !entryId) throw new Error('No journal entry requested')
      const response = await api.GET('/accounting/journal-entries/{entry}', {
        headers: { 'X-Company-Id': companyId },
        params: { path: { entry: entryId } },
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to load journal entry')
      return unwrap<JournalEntry>(response.data ?? {}, 'journal-entry')
    },
  })
}

export function usePostJournalEntry(
  companyId: string | null,
  options?: Omit<UseMutationOptions<JournalEntry, Error, JournalEntryBody>, 'mutationFn'>,
) {
  const queryClient = useQueryClient()
  const currentCompany = useFreshCompanyId(companyId)
  return useMutation<JournalEntry, Error, JournalEntryBody>({
    ...options,
    mutationFn: async (body) => {
      const response = await api.POST('/accounting/journal-entries', {
        headers: { 'X-Company-Id': currentCompany() },
        body: body as components['schemas']['JournalEntryStoreRequest'],
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to post entry')
      return unwrap<JournalEntry>(response.data ?? {}, 'journal-entries')
    },
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({
        queryKey: ['companies', companyId, 'accounting'],
      })
      options?.onSuccess?.(...args)
    },
  })
}

export interface ReverseEntryBody {
  entryId: string
  /** Required by the API — a reversal without a stated cause is not auditable. */
  reason: string
}

export function useReverseJournalEntry(
  companyId: string | null,
  options?: Omit<UseMutationOptions<JournalEntry, Error, ReverseEntryBody>, 'mutationFn'>,
) {
  const queryClient = useQueryClient()
  const currentCompany = useFreshCompanyId(companyId)
  return useMutation<JournalEntry, Error, ReverseEntryBody>({
    ...options,
    mutationFn: async ({ entryId, reason }) => {
      const response = await api.POST('/accounting/journal-entries/{entry}/reverse', {
        params: { path: { entry: entryId } },
        headers: { 'X-Company-Id': currentCompany() },
        body: { reason },
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to reverse entry')
      return unwrap<JournalEntry>(response.data ?? {}, 'journal-entries')
    },
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({
        queryKey: ['companies', companyId, 'accounting'],
      })
      options?.onSuccess?.(...args)
    },
  })
}
